/**
 * Domain konten.
 *
 * Keduanya menerima JWT sejak 8 September: `GET /dashboard/content-queue` memang
 * sudah, dan penjaga `PATCH /content-queue/{id}/review` diperluas dari
 * `require_api_key` jadi `require_dashboard_or_service`. Tombol Telegram tidak
 * berubah — X-API-Key tetap diperiksa lebih dulu.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, kueri } from '../../shared/api/client'

export const kunci = {
  antrean: (saring) => ['konten', 'antrean', saring],
}

export function useAntreanKonten(saring = {}) {
  return useQuery({
    queryKey: kunci.antrean(saring),
    queryFn: () => api.get(`/dashboard/content-queue${kueri(saring)}`),
  })
}

/**
 * Setujui atau tolak satu konten.
 *
 * Antrean disegarkan sesudahnya alih-alih ditambal optimistis: dua orang bisa
 * meninjau baris yang sama dari dua tempat (di sini dan di Telegram), dan rutenya
 * memang idempoten — menekan dua kali mengembalikan status apa adanya. Menambal
 * optimistis akan menampilkan hasil yang belum tentu jadi milik penekannya.
 */
export function useTinjauKonten() {
  const qc = useQueryClient()
  return useMutation({
    // SEMUA TUJUAN SEKALIGUS (`ids`), sama dengan `useTanganiGagal`. Satu konten IG+FB
    // adalah dua baris; versi pertama hanya meninjau anak pertama, jadi Setujui/Tolak
    // pada baris gabungan meninggalkan platform kedua tetap menunggu (29 Sep).
    mutationFn: async ({ id, ids, keputusan }) => {
      const hasil = []
      for (const x of ids ?? [id]) {
        hasil.push(await api.patch(`/dashboard/content-queue/${x}/review`,
                                   { decision: keputusan }))
      }
      return hasil
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}

/** Bahan hasil scraping, TERMASUK skor dan alasannya — dua kolom yang tersimpan sejak
 *  awal dan sampai 10 September tidak dibaca satu layar pun. */
/**
 * Konten yang gagal terbit: coba lagi, atau buang dari antrean.
 *
 * MENERIMA BANYAK ID SEKALIGUS, dan itu bukan keluwesan yang tidak dipakai.
 *
 * Satu konten yang terbit ke Instagram DAN Facebook adalah DUA baris di antrean — tiap
 * platform bisa gagal sendiri-sendiri. Layar menggabungkannya jadi satu baris, jadi satu
 * tekan "Buang" harus membuang KEDUANYA.
 *
 * Versi pertama cuma mengirim id anak pertama. Akibatnya: sesudah satu klik, satu baris
 * jadi `rejected` sementara yang lain tetap `failed` — barisnya tetap muncul di layar, dan
 * orang harus menekan tombol yang sama dua kali tanpa tahu kenapa. Operator melaporkannya
 * dengan kalimat "kok buangnya 2 kali ya?".
 *
 * Dijalankan BERURUTAN, bukan serentak: keduanya menulis baris yang berbeda tapi lewat
 * satu sesi database yang sama, dan permintaan yang saling menyusul lebih mudah dibaca
 * saat salah satunya gagal.
 */
export function useTanganiGagal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ ids, aksi }) => {
      const hasil = []
      for (const id of ids) {
        hasil.push(await api.patch(`/dashboard/content-queue/${id}/gagal`, { aksi }))
      }
      return hasil
    },
    // Sekali di akhir, bukan per id: memuat ulang daftar di tengah membuat barisnya
    // berkedip separuh-hilang.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['konten', 'antrean'] }),
  })
}

/**
 * Ambil ULANG angka insight satu konten dari platformnya.
 *
 * SATU PLATFORM PER PANGGILAN, tidak digabung seperti `useTanganiGagal`. Alasannya
 * berbeda: membuang konten harus mengenai semua tujuannya sekaligus, sementara angka
 * dibaca satu platform pada satu waktu — yang dilihat orang cuma panel yang sedang
 * terbuka, dan menembak Meta untuk platform yang tidak dilihat menghabiskan kuota yang
 * dipakai menerbitkan.
 */
export function useSegarkanInsight() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.post(`/dashboard/content-queue/${id}/insight`),
    // Daftar DAN detailnya: angkanya dibaca kartu "Angka per platform" di detail konten,
    // dan sampai 5 Oktober hanya daftarnya yang disegarkan, jadi angka baru muncul sesudah
    // orangnya keluar lalu masuk lagi. Ditunggu, supaya kabar "disegarkan" datang bersama angkanya.
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['konten', 'antrean'] }),
      qc.invalidateQueries({ queryKey: ['konten', 'satu'] }),
    ]),
  })
}

export function useBahan(limit = 50) {
  return useQuery({
    queryKey: ['konten', 'bahan', limit],
    queryFn: () => api.get(`/dashboard/bahan?limit=${limit}`),
  })
}

export function useBuatKonten() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.post('/dashboard/buat-konten', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}

/** Memicu scraping di luar jadwal. Membakar kuota Serper dan menimpa "Hasil Terkini",
 *  jadi ia tindakan yang disengaja — bukan tombol muat-ulang. */
export function usePicuScraping() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post('/dashboard/trigger/scraping', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['konten', 'bahan'] }),
  })
}

/** Tombol generate. Rutenya sudah ada sejak lama; yang berubah 10 September penjaganya —
 *  dari `require_api_key` (yang tidak dipunyai browser) jadi `require_dashboard_or_api_key`. */
/**
 * Caption + pesan kunci + hashtag dalam SATU panggilan (ADR-077).
 *
 * MENGGANTIKAN tiga panggilan yang dirakit dari berkas locale (`bk.promptCaption`,
 * `bk.promptPesan`, `bk.promptHashtag`). Dua akibat bentuk lama: pesan kunci dan hashtag
 * disusun DARI caption yang sudah jadi — bukan dari gagasan yang sama — jadi ketiganya bisa
 * saling melenceng; dan mengubah aturan caption menuntut rilis frontend.
 *
 * `jumlah: 3` mengembalikan tiga pilihan yang BERBEDA SUDUT, tetap satu panggilan. Tiap
 * pilihan membawa `sudut` supaya kartu di layar bisa menyebut apa bedanya — tiga kartu tanpa
 * label bedanya adalah tiga kartu yang harus dibaca semuanya.
 */
export function useTulisIsiKonten() {
  return useMutation({
    // `arah` (ADR-108): `umum`, atau `produk` dan `iklan` yang membaca Pengetahuan. Dikirim
    // hanya kalau bukan bawaannya, supaya permintaan yang lama tetap persis seperti semula.
    // `produk` (ADR-113): dokumen produk yang dipilih orangnya untuk iklan ini; hanya dikirim
    // bersama arah iklan. Tanpa itu, produknya dicari dari judulnya seperti semula.
    mutationFn: ({ judul, topik = '', jumlah = 1, arah = 'umum', produk = '' }) =>
      api.post('/text/caption', {
        judul, topik, jumlah,
        ...(arah !== 'umum' ? { arah } : {}),
        ...(arah === 'iklan' && produk ? { produk } : {}),
      }),
  })
}

export function useBuatTeks() {
  return useMutation({
    mutationFn: ({ system_prompt = '', user_prompt, max_tokens = 600 }) =>
      api.post('/text/generate', { system_prompt, user_prompt, max_tokens }),
  })
}

/**
 * Tulis prompt gambar dari caption — SATU mesin, dipakai bersama jalur terjadwal (ADR-075).
 *
 * MENGGANTIKAN `useBuatTeks` UNTUK TOMBOL "Buatkan prompt dari caption". Bentuk lama
 * memanggil `/text/generate` dengan dua kalimat yang hidup di `locales/id.json`
 * (`bk.promptPrompt`), sementara jalur terjadwal memakai blok aturan ±20 baris di
 * `content/brain.py`. Keduanya mengisi kolom yang sama dan tidak satu pun menyebut yang
 * lain — dan aturan prompt yang hidup di berkas locale berarti mengubahnya menuntut
 * rilis frontend.
 *
 * Yang dikembalikan BUKAN cuma teks: `referensi` adalah id bahan yang dipilih mesin
 * (urut, yang pertama paling menentukan) dan `masalah` adalah pelanggaran yang masih
 * tersisa sesudah satu kali perbaikan. Keduanya ditampilkan, bukan dibuang — prompt
 * cacat yang terlihat sempurna sampai gambarnya dibayar adalah keadaan terburuk.
 */
export function useBuatPromptGambar() {
  return useMutation({
    mutationFn: ({ caption = '', key_message = '', brief = '', slide = 1, bahan = [], prompt_id = null,
                   ikut_referensi = false, ragam = null, topik = '' }) =>
      // `bahan` bentuknya SAMA dengan `useBuatGambar`, jadi layar mengirim daftar yang sama
      // ke keduanya. `slide` > 1 mengembalikan satu prompt per slide dari SATU panggilan —
      // slide harus maju berurutan, dan N panggilan menghasilkan N adegan tak berhubungan.
      // `prompt_id`: referensi konten yang dipakai (ADR-109); tanpa itu, prompt bawaan tenant.
      // `ikut_referensi` (ADR-113): gambar yang dilihat penulis adalah gambar referensinya,
      // didahului kanvas berbingkai; `bahan` tinggal gambar tambahan orangnya, dan `slide`
      // jumlah slide yang digambar. `ragam`: susunan yang sudah dipakai konten ini.
      api.post('/image/prompt', {
        caption, key_message, brief, slide, bahan,
        ...(prompt_id ? { prompt_id } : {}),
        ...(ikut_referensi ? { ikut_referensi: true } : {}),
        ...(ragam ? { ragam } : {}),
        ...(topik ? { topik } : {}),
      }),
  })
}

/**
 * Setujui KONSEP dari layar, bukan cuma dari Telegram.
 *
 * Rutenya sama persis dengan yang dipanggil tombol Setujui di Telegram
 * (`03b → POST /campaigns/{id}/jadikan-draf`), dan idempoten — dua orang yang
 * menekannya dari dua tempat tidak melahirkan antrean kedua.
 *
 * Tanpa ini konsep hanya bisa disetujui dari HP, dan layar Konten cuma bisa
 * MENAMPILKAN pekerjaan yang tidak bisa diselesaikannya.
 */
export function useSetujuiKonsep() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (campaignId) => api.post(`/campaigns/${campaignId}/jadikan-draf`, {}),
    onSettled: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}

export function useBuatGambar() {
  return useMutation({
    // 1024x1024 hanya untuk model yang katalognya belum tahu rasionya — lihat catatan di
    // `ImageGenerateRequest`. Yang tahu memakai `aspect_ratio`.
    mutationFn: ({ prompt, width = 1024, height = 1024, model = null, referensi = [],
                   bahan = [], aspect_ratio = null, bingkai = null }) =>
      api.post('/image/generate', {
        prompt, width, height, created_by: 'dashboard',
        // Bingkai referensi konten (ADR-111): hasilnya disusun seperti di rantai slot.
        ...(bingkai ? { bingkai } : {}),
        // Kosong = bawaan model (ADR-073). Hanya dikirim bersama `model`: backend menolak
        // rasio tanpa model, karena tiap model punya daftar sendiri.
        ...(aspect_ratio && model ? { aspect_ratio } : {}),
        // BENTUK BARU: satu daftar berurutan untuk dua sumber — referensi tersimpan
        // milik admin dan gambar temporary milik pemakai layar. `referensi` di bawah
        // dipertahankan untuk pemanggil lama (n8n) yang belum mengirim `bahan`.
        ...(bahan.length ? { bahan } : {}),
        // Tingkat 3 (ADR-054). Kosong = pakai rantai pilihan tenant. Divalidasi backend
        // terhadap `tenant_ai_models` — bukan dipercaya dari sini.
        ...(model ? { model } : {}),
        // ID, bukan URL. Backend yang mengubahnya jadi berkas, lewat ScopedRepository —
        // jadi referensi milik tenant lain sekadar tidak ketemu.
        ...(referensi.length ? { referensi } : {}),
      }),
  })
}

/**
 * MENGUBAH gambar yang sudah ada, bukan membuat yang baru.
 *
 * `sumber` dan `tiket` datang BERSAMA dari server saat gambarnya dibuat atau diunggah.
 * Tiketnya tanda tangan yang menyatakan berkas itu milik tenant ini — tanpa itu backend
 * menolak 403, karena berkas di `/static/generated` tidak punya kolom pemilik dan URL-nya
 * bisa dibaca siapa pun yang punya tautannya.
 *
 * `instruksi` adalah PERUBAHANNYA ("ganti latarnya jadi biru"), bukan deskripsi lengkap
 * gambarnya. Deskripsi lengkap membuat model menggambar ulang alih-alih menyunting.
 */
export function useUbahGambar() {
  return useMutation({
    // `bingkai` (ADR-113): bingkai referensi kontennya. Server menempelkannya lagi hanya pada
    // gambar yang memang hasil susunan berbingkai; gambar unggahan tidak ditempeli.
    mutationFn: ({ sumber, tiket, instruksi, model = null, referensi = [], bahan = [], bingkai = null }) =>
      api.post('/image/ubah', {
        sumber, tiket, instruksi,
        ...(bingkai ? { bingkai } : {}),
        ...(bahan.length ? { bahan } : {}),
        ...(model ? { model } : {}),
        ...(referensi.length ? { referensi } : {}),
      }),
  })
}

/**
 * Membuat video. MENUNGGU SAMPAI SELESAI — satu sampai empat menit.
 *
 * Tidak ada job id dan tidak ada polling di sini: `proxy_read_timeout 300s` di nginx
 * memang sudah dipasang untuk ini, dan batas tunggu di backend berada di bawahnya supaya
 * yang menjawab tetap aplikasinya, bukan proxy dengan 504 tanpa keterangan.
 */
export function useBuatVideo() {
  return useMutation({
    mutationFn: ({ prompt, model = null, durasi = 6 }) =>
      api.post('/video/generate', {
        prompt, durasi, created_by: 'dashboard',
        // Divalidasi backend terhadap `tenant_ai_models` — bukan dipercaya dari sini.
        ...(model ? { model } : {}),
      }),
  })
}

/* ── Referensi gambar (migrasi 0029) ────────────────────────────────────────
 * Bahan yang DIBERIKAN marketing supaya hasil generasi mengikuti gaya brand —
 * masukan, bukan keluaran. Hidup di tenant, bukan di satu konten: satu berkas
 * "palet-brand.png" dipakai lusinan konten selama berbulan-bulan.
 */
const KUNCI_REF = ['konten', 'referensi']

/**
 * Ke mana konten BENAR-BENAR bisa terbit untuk tenant ini.
 *
 * Dulu daftar ini dipatok di dalam `BuatKonten.jsx` dengan Facebook dimatikan permanen.
 * Benar selama adapternya belum ada, dan diam-diam salah begitu ada: tombolnya tetap
 * mati dan tidak ada yang tahu kenapa selain membaca kodenya.
 */
export function useTujuan({ nyala = true } = {}) {
  return useQuery({
    queryKey: ['konten', 'tujuan'],
    queryFn: () => api.get('/dashboard/tujuan'),
    staleTime: 60_000,
    enabled: nyala,
  })
}

/**
 * Tambahkan platform ke konten yang belum selesai terbit. Baris barunya kembaran saudaranya
 * yang belum terbit: ikut terjadwal di jam yang sama, ikut menunggu, atau ikut jadi draf.
 */
export function useTambahPlatform() {
  const qc = useQueryClient()
  return useMutation({
    // `sekarang` hanya untuk konten yang semua platformnya sudah tayang: platform barunya
    // diterbitkan saat itu juga. Selagi masih ada yang belum terbit, backend tidak membacanya.
    mutationFn: ({ id, platforms, sekarang = false }) =>
      api.post(`/dashboard/content-queue/${id}/platform`, { platforms, ...(sekarang ? { sekarang: true } : {}) }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}

const KUNCI_REFPROMPT = ['konten', 'prompt-template']

/**
 * Referensi prompt gambar — pustaka gaya yang DIPILIH MESIN (ADR-079).
 *
 * MEMBACA terbuka untuk marketing: layar Buat Konten menampilkan referensi mana yang
 * dipilih mesin untuk sebuah prompt, dan nama tanpa isinya tidak bisa dinilai siapa pun.
 * MENGUBAH dikunci `tenant_admin` di backend, sama seperti referensi gambar — teks ini
 * menentukan tampilan setiap gambar tenant, termasuk yang terbit tanpa ditinjau.
 *
 * Kunci cache-nya tetap `prompt-template`, sama dengan jalur rutenya. Yang berubah cuma
 * labelnya di layar.
 */
export function useReferensiPrompt() {
  return useQuery({
    queryKey: KUNCI_REFPROMPT,
    queryFn: () => api.get('/dashboard/prompt-template'),
    staleTime: 60_000,
  })
}

export function useBuatReferensiPrompt() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.post('/dashboard/prompt-template', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KUNCI_REFPROMPT }),
  })
}

export function useUbahReferensiPrompt() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }) => api.put(`/dashboard/prompt-template/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KUNCI_REFPROMPT }),
  })
}

export function useHapusReferensiPrompt() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.del(`/dashboard/prompt-template/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KUNCI_REFPROMPT }),
  })
}

/**
 * Uji tulisan sebuah referensi konten dari isiannya, termasuk yang belum disimpan (ADR-109):
 * caption contoh dan prompt gambar tiap slide. Teks saja, dan tidak ada yang tersimpan:
 * hasilnya tidak masuk ke Konten dan tidak dikirim ke Telegram. Gambar contohnya dibuat
 * `useBuatGambar`, sama dengan Buat konten.
 */
export function useUjiTulisanReferensi() {
  return useMutation({ mutationFn: (body) => api.post('/dashboard/prompt-template/uji-tulisan', body) })
}

export function useReferensi() {
  return useQuery({
    queryKey: KUNCI_REF,
    queryFn: () => api.get('/dashboard/referensi'),
    staleTime: 60_000,
  })
}

export function useUnggahReferensi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (berkas) => api.unggah('/dashboard/referensi', berkas),
    onSuccess: () => qc.invalidateQueries({ queryKey: KUNCI_REF }),
  })
}

/**
 * Menandai referensi sebagai bahan brand, atau mengembalikannya jadi sekali pakai.
 *
 * Bisa bolak-balik. Label ini penilaian orang, bukan fakta yang diturunkan dari data —
 * satu foto produk bisa mulai sebagai bahan sekali pakai lalu ternyata dipakai terus.
 * Label yang cuma bisa dipasang sekali membuat orang ragu memasangnya sama sekali.
 */
export function useJenisReferensi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, jenis }) => api.patch(`/dashboard/referensi/${id}/jenis`, { jenis }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KUNCI_REF }),
  })
}

/**
 * Koreksi deskripsi referensi yang ditulis model vision (ADR-075).
 *
 * Deskripsi menentukan apakah bahan ini DIPILIH mesin prompt untuk konten yang mana,
 * jadi salahnya tidak kelihatan sampai gambar yang salah keluar. Model vision menyebut
 * logo sebagai "lingkaran biru dengan huruf" — benar sebagai deskripsi, tidak berguna
 * sebagai bahan keputusan. Yang mengunggahnya tahu itu logo.
 *
 * Mengirim string KOSONG sah: itu cara menyatakan "jangan pilih bahan ini otomatis"
 * tanpa memensiunkannya — manusia tetap bisa memilihnya sendiri.
 */
/**
 * Ganti NAMA bahan gambar.
 *
 * Namanya ikut dikirim ke penulis prompt sebagai label bahan, dan `kebijakan_referensi`
 * menyebut bahan dengan nama itu. Sampai 21 September namanya dipatok nama berkas saat
 * diunggah, jadi bahan bernama `ad5df22c-1e37-….jpg` tidak bisa disebut kebijakan mana pun.
 */
export function useNamaReferensi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, nama }) =>
      api.patch(`/dashboard/referensi/${id}/nama`, { nama }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['konten', 'referensi'] }),
  })
}

export function useDeskripsiReferensi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, deskripsi }) =>
      api.patch(`/dashboard/referensi/${id}/deskripsi`, { deskripsi }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KUNCI_REF }),
  })
}

/** Memensiunkan, bukan menghapus — berkasnya tetap ada supaya riwayat tidak berbohong. */
export function usePensiunReferensi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.del(`/dashboard/referensi/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KUNCI_REF }),
  })
}

/**
 * Menyunting media konten yang masih MENUNGGU TINJAUAN.
 *
 * Sampai 14 September tinjauan cuma punya dua jawaban: setujui atau tolak. Foto yang
 * hampir benar tidak punya jalan tengah, jadi satu-satunya cara memperbaikinya adalah
 * menolaknya lalu membuat ulang seluruh konten dari nol.
 *
 * Ketiganya menyegarkan SELURUH daftar konten, bukan satu baris: satu slot melahirkan
 * satu baris per tujuan, dan backend mengubah semuanya sekaligus supaya Instagram dan
 * Facebook tidak menerbitkan gambar yang berbeda.
 */
function segarkanKonten(qc) {
  qc.invalidateQueries({ queryKey: ['konten'] })
}

/**
 * Simpan susunan slide hasil penyuntingan — SELURUH daftarnya, bukan selisihnya.
 *
 * Panel penyunting memegang seluruh daftar di layar (urutan, prompt, model), jadi
 * mengirim selisih berarti dua sisi harus sepakat soal urutan — dan urutan collase
 * justru hal yang sedang diubah orangnya.
 */
/**
 * Yang dibutuhkan panel Sunting gambar untuk konten yang sudah ada (ADR-113): tiket tiap
 * gambarnya (supaya bisa diubah dengan arahan), referensi dan bingkai konsepnya, prompt tiap
 * slide, dan susunannya. Dibaca tiap panelnya dibuka; tidak disimpan lama.
 */
export function useBahanSunting(id) {
  return useQuery({
    queryKey: ['konten', 'sunting', id],
    queryFn: () => api.get(`/dashboard/content-queue/${id}/sunting-gambar`),
    enabled: Boolean(id),
    staleTime: 0,
    gcTime: 0,
  })
}

export function useSimpanSlide() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, urls }) =>
      api.put(`/dashboard/content-queue/${id}/slide`, { urls }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}

/**
 * Unggah gambar sendiri jadi salah satu slide — dipakai Buat Konten DAN detail konten.
 *
 * Mengembalikan URL-nya dan TIDAK menyentuh antrean. Di detail konten barisnya berubah
 * saat Simpan; di Buat Konten barisnya bahkan belum lahir. Itu yang membuat tombol Batal
 * berarti sesuatu di kedua layar — kalau unggahan langsung menulis, "batal" sesudahnya
 * tidak bisa mengembalikan apa pun.
 *
 * `id` OPSIONAL: diisi saat menyunting konten yang sudah ada (backend menegakkan bahwa
 * kontennya masih boleh disunting), dikosongkan saat membuat konten baru.
 */
export function useUnggahGambarKonten() {
  return useMutation({
    mutationFn: ({ id, berkas }) =>
      api.unggah(`/dashboard/gambar-unggah${id ? `?konten=${id}` : ''}`, berkas),
  })
}

/**
 * Unggah video sendiri (ADR-114): MP4 tegak 9:16, 3 sampai 90 detik, paling besar 300 MB.
 * Sama dengan unggah gambar: mengembalikan alamatnya dan tidak menyentuh antrean.
 */
export function useUnggahVideoKonten() {
  return useMutation({
    mutationFn: ({ id, berkas }) =>
      api.unggah(`/dashboard/video-unggah${id ? `?konten=${id}` : ''}`, berkas),
  })
}

/** Unggah gambar sampul video (ADR-114). Mengembalikan alamat JPEG-nya; belum memasangnya. */
export function useUnggahSampulKonten() {
  return useMutation({
    mutationFn: ({ id, berkas }) =>
      api.unggah(`/dashboard/sampul-unggah${id ? `?konten=${id}` : ''}`, berkas),
  })
}

/** Pasang, ganti, atau lepas (`null`) sampul konten video, untuk semua platformnya. */
export function useSimpanSampul() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, sampul_url }) =>
      api.put(`/dashboard/content-queue/${id}/sampul`, { sampul_url: sampul_url ?? null }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}

/** Batalkan jadwal: konten terjadwal kembali jadi draf tanpa jam (operator 13 Okt). */
export function useBatalJadwal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.post(`/dashboard/content-queue/${id}/batal-jadwal`),
    onSettled: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}

/**
 * Sunting caption, pesan kunci, dan hashtag konten yang masih menunggu tinjauan.
 *
 * Sampai 14 September ketiganya tidak bisa disentuh dari mana pun. Rantai manual
 * Telegram punya `update_caption`, tapi ia menulis ke DRAFT CAMPAIGN — sementara baris
 * antrean menyalin captionnya saat dibuat, jadi mengubah draft sesudah barisnya lahir
 * tidak mengubah apa pun yang akan terbit.
 */
export function useUbahTeksKonten() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...isi }) =>
      api.patch(`/dashboard/content-queue/${id}/teks`, isi),
    onSettled: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}

/**
 * Naikkan konten DRAFT jadi terjadwal.
 *
 * Draft sebelumnya jalan buntu: `publish_due` hanya memilih `scheduled`, `review_item`
 * menolak apa pun yang bukan `pending_approval`, dan `tangani_gagal` hanya menyentuh
 * `failed`. Barisnya duduk di "Akan tayang" dengan tanggal yang terlihat sah, dan tidak
 * pernah terbit — tanpa galat, tanpa peringatan.
 */
export function useJadwalkanKonten() {
  const qc = useQueryClient()
  return useMutation({
    // `platforms` dan `sekarang` milik dialog Jadwalkan rancang ulang (ADR-092): hanya
    // platform yang dicentang, dan Upload sekarang. Pemanggil lama hanya mengirim jamnya.
    mutationFn: ({ id, ...isi }) => api.patch(`/dashboard/content-queue/${id}/jadwalkan`, isi),
    onSettled: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}

/** Jam slot terbit berikutnya yang masih kosong, paling banyak empat: pilihan di dialog Jadwalkan. */
export function useSlotKosong(aktif = true) {
  return useQuery({
    queryKey: ['konten', 'slot-kosong'],
    queryFn: () => api.get('/dashboard/slot-kosong'),
    enabled: aktif,
    // Slot yang ditawarkan harus yang kosong SEKARANG, bukan saat dialog terakhir dibuka.
    staleTime: 0,
  })
}

/**
 * Satu konten menurut id salah satu barisnya, dengan baris semua platformnya. `aktif` false
 * menghentikan penyegarannya tanpa membuang isinya: dipakai selagi konten itu dihapus,
 * supaya layar tidak meminta lagi sesuatu yang baru saja dibuangnya.
 */
export function useKontenSatu(id, aktif = true) {
  return useQuery({
    queryKey: ['konten', 'satu', id],
    queryFn: () => api.get(`/dashboard/content-queue/${id}`),
    enabled: !!id && aktif,
  })
}

/**
 * Tolak satu konten (konsep, atau konten yang menunggu) di semua platformnya, dan
 * pembaliknya. Tolak mengembalikan `ditolak`: id baris yang ditolak, yang dikirim balik
 * untuk membatalkannya.
 */
export function useTolakKonten() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.post(`/dashboard/content-queue/${id}/tolak`, {}),
    onSettled: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}

/**
 * Edit prompt di tahap konsep (ADR-093): prompt ditulis ulang dari arahan orangnya, tanpa
 * membuat gambar. Mengembalikan `prompt_gambar` yang baru.
 */
export function usePerbaruiPrompt() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, arahan }) => api.post(`/dashboard/content-queue/${id}/prompt`, { arahan }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}

/**
 * Regenerate gambar (tanpa arahan), atau Edit prompt pada konten yang sudah bergambar
 * (dengan arahan). Berbayar: gambar baru dibuat, dan bisa makan setengah menit.
 */
export function useGambarLagi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, arahan = '' }) => api.post(`/dashboard/content-queue/${id}/gambar-lagi`, { arahan }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}

export function useBatalTolak() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ids }) => api.post(`/dashboard/content-queue/${id}/batal-tolak`, { ids }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}

/**
 * Buang satu konten dari Smova, PERMANEN — beserta seluruh tujuannya.
 *
 * Konten yang sudah tayang sebelumnya tidak bisa disentuh sama sekali: `review_item`
 * hanya menerima `pending_approval`, `tangani_gagal` hanya `failed`. Jadi saat
 * postingannya diturunkan dari Instagram dan Facebook, barisnya tetap mengaku tayang.
 */
export function useHapusKonten() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.del(`/dashboard/content-queue/${id}`),
    onSettled: () => qc.invalidateQueries({ queryKey: ['konten'] }),
  })
}
